import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { OAuthEvent, OAuthService } from 'angular-oauth2-oidc';
import moment from "moment";
import { Observable, Subscription, combineLatest } from 'rxjs';
import { delay, filter, map, take } from 'rxjs/operators';
import { SystemConfigValue } from 'src/app/admin/system-configuration/shared/models/system-config-value';
import { Globals } from 'src/app/core/services/globals';
import { UserSigninStatusModel } from 'src/app/lobby/shared/models/lobby-user-signin-status.model';
import { UserSigninStatus } from 'src/app/shared/enums/UserSigninStatus.enum';
import { ConfigurationGroup } from 'src/app/shared/enums/configurationGroup.enum';
import { SystemConfigurationName } from 'src/app/shared/enums/systemConfigurationName.enum';
import { AppConfigurations } from 'src/app/shared/models/appConfiguration.model';
import { AppInitService } from 'src/app/app-init.service';
import { SessionService } from 'src/app/shared/services/session.service';
import { SyncService } from 'src/app/shared/services/sync.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { getAvailableTenants, getCurrentTenant, getLoggedInUserInfo } from 'src/app/state';
import { State } from 'src/app/state/app.state';
import { TenantModel } from '../../../../core/models/tenant';
import { GlobalDataService } from '../../../../shared/services/global-data.service';
import * as CoreActions from './../../../../state/actions/core.actions';
import {
  GetUserPermissionList,
  getACLicenseStatus,
  getClientCreationStatus,
  getLTLicenseStatus,
  getLobbyLocationSelected,
  getLocationSelected,
  getLoggedInUserId,
  getSignedInStatus,
  getSyncStatus,
  getSystemConfigurations,
  getUserFullName,
  getACLocationCodeSelected
} from './../../../../state/index';

import { BCNotificationService } from './services/bcnotification.service';
import { getBcsNotifications } from './state';
import * as AppHeaderActions from './state/actions/app-header.actions';
import { NotificationMessageModel } from './state/app-header.reducer';
import { TranslateService } from '@ngx-translate/core';
import { TranslationLoaderFacade } from 'src/app/admin/localization/translation-loader.facade';

export interface SyncResponseStatus {
  type: string,
  errors: any,
  resource?: string,
  success?: number,
  failed?: number,
  status?: string
  errorMessages: string[]
}

interface ErrorObj {
  id: string,
  errors: string[]
}
interface SyncValidationError {
  resource: string,
  errorObjs: ErrorObj[]
}

@Component({
  selector: 'app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss']
})

export class AppHeaderComponent implements OnInit, OnDestroy {

  isACLicenseEnabled$: Observable<boolean> = this.store.select(getACLicenseStatus);
  isLTLicenseEnabled$: Observable<boolean> = this.store.select(getLTLicenseStatus);
  bcsNotifications$: Observable<NotificationMessageModel> = this.store.select(getBcsNotifications);
  alertCount: number = 0;
  loggedUserName: string = '';
  loggedUserId: number = 0;
  loggedUserTzOffset: number = 0;
  title = 'app';
  lobbyConfigurations: any;
  public tenantChangeOpened = false;
  public availableTenants: TenantModel[] = [];
  public showTenantChangeOption: boolean = false;
  public selectedTenant: TenantModel = {
    isDefault: false,
    tenantID: 0,
    tenantDisplayName: ''
  };
  public showAlertPopup = false;
  private alertReadingSubscription: Subscription = new Subscription();
  
  private locationCode: string = '';
  private appConfig!: AppConfigurations;
  private acUrl: string = '';

  public userPermissionList: number[] = [];
  private subs: Subscription = new Subscription();
  private unsignedStatus?: UserSigninStatusModel;
  public locationLastAutoWrapUpTime: string = '';

  public syncEnabled = false;
  public syncInProgress = false;
  public currentSyncStatus: SyncResponseStatus = {
    errorMessages: [],
    errors: null,
    type: ''
  };
  private syncHasErrors: boolean = false;
  public syncConfPopupOpen: boolean = false;
  public showSyncValidationResultPopup: boolean = false;
  public validationErrorList: SyncValidationError[] = [];
  public syncInProgressMessage: string = '';
  public manualSyncMessage: string = '';

  public currentClientCreationStatus: string = '';
  private userPermissionGroup: string = '';
  public isDefaultPermissionGroup: boolean = false;
  public clientToolTip: string = '';
  public prevSignInLocationId: number = 0;
  public prevTenantId: number = 0;
  private currentPreferredLanguageId?: number;

  constructor(private router: Router,
    private store: Store<State>,
    private globals: Globals,
    public gloabDataService: GlobalDataService,
    private oauthService: OAuthService,
    private session: SessionService,
    private notificationService: BCNotificationService,
    private syncService: SyncService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private translationLoaderFacade: TranslationLoaderFacade,
    private appInitService: AppInitService
  ) { } 

  ngOnInit() {
    this.syncInProgressMessage = this.translateService.instant('_Shared_App_State_Sync_InProcess_Text_');
    this.manualSyncMessage = this.translateService.instant('_Shared_App_State_manual_Sync_Text_');
    
    this.alertCount = 0;
    this.loggedUserId = 0;
    this.loggedUserTzOffset = -1;
    this.store.select(GetUserPermissionList).subscribe((allUserPermissions) => {
      if (allUserPermissions) {
        this.userPermissionList = allUserPermissions;
      }
    });

    const appConfigSub = this.appInitService.getAppConfiguration$().subscribe({
      next: (config) => {
        this._onReceiveAppConfig(config);
      }
    })

    // -- Tenant switching related setups
    this.availableTenants = [];
    this.showTenantChangeOption = false;
    this.subs.add(this.oauthService.events.pipe(delay(500)).subscribe((event: OAuthEvent) => {
      this._onReceiveOAuthEvents(event);
    }));
    this.subs.add(this.store.select(getUserFullName).subscribe(username => this._onReceiveUserName(username)));

    this.subs.add(this.store.select(getUserFullName).subscribe(username => this._onReceiveUserName(username)));
    this.store.select(getSystemConfigurations).subscribe(
      (configurations) => {
        this.lobbyConfigurations = configurations.filter(c => c.configurationGroup === ConfigurationGroup.LobbySettings);
        if (this.lobbyConfigurations.length > 0) {
          const isAlertPopupEnable = this.lobbyConfigurations
            .find((x: SystemConfigValue) => x.configurationName == SystemConfigurationName.EnableAlertPopups).value;
          this.showAlertPopup = isAlertPopupEnable == "1";
        }
      });

    this.subs.add(this.store.select(getLoggedInUserId).subscribe(userId => {
      if (userId) {
        this._onReceiveUserId(userId)
      }
    }));

    this.subs.add(this.store.select(getLobbyLocationSelected).subscribe(loc => {
      if (loc.locationId != this.prevSignInLocationId) {
        this._onSignInLocationChanged(loc.lastLocationAutoWraupTime || '');
        this.prevSignInLocationId = loc.locationId;
      }
    }));

    this.subs.add(this.store.select(getSyncStatus).subscribe(status => {
      this.currentSyncStatus = status;
      this.syncStatus(this.currentSyncStatus);
    }));

    this.subs.add(this.store.select(getClientCreationStatus).subscribe(status => {
      this.currentClientCreationStatus = status;
    }));

    const selectedTenantSub = this.store.select(getCurrentTenant)
      .pipe(filter(tenant => tenant != null))
      .subscribe(tenant => this._onSelectedTenantChange(tenant));
    const availableTenantsSub = this.store.select(getAvailableTenants)
      .subscribe(tenants => this._onAvailableTenantsChange(tenants));
    const uerLoggedSubscription = this.store.select(getLoggedInUserInfo)
      .subscribe((loggedInUserInfo) => {
        if (loggedInUserInfo == null) {
          return;
        }
        if (this.currentPreferredLanguageId != null
          && this.currentPreferredLanguageId !== loggedInUserInfo.preferredLanguage) {
          this.translationLoaderFacade.refreshTranslations().subscribe();
        }
        this.currentPreferredLanguageId = loggedInUserInfo.preferredLanguage;
        this.loggedUserTzOffset = loggedInUserInfo.prefferedTimeZone.utcOffset;
        this._subscribeToSyncStatus(loggedInUserInfo.tenantId, loggedInUserInfo.userId);
        this.userPermissionGroup = loggedInUserInfo.permissionGroup || '';
        this.isDefaultPermissionGroup = this.userPermissionGroup === 'FMSI Default Group';
      });

    const locationCodeSubscription = this.store.select(getACLocationCodeSelected)
      .subscribe((selectedLocation) => {
        if (selectedLocation != null) {
          this.locationCode = selectedLocation;
        }
    });

    this.subs.add(selectedTenantSub);
    this.subs.add(availableTenantsSub);
    this.subs.add(uerLoggedSubscription);
    this.subs.add(locationCodeSubscription);
    // -- Tenant switching realted setups end
  }

  private _onReceiveAppConfig(appConfig: AppConfigurations) {
    this.appConfig = appConfig;
    this.acUrl = this.appConfig.ac.acPortalUrl;
  }

  private syncInitialization(currentTenantId: string) {
    const syncConfig = this.syncService.getSyncFeatureConfig();
    this.syncEnabled = syncConfig.enabledClients.includes(currentTenantId) && (syncConfig.forceDisable !== 'true');
  }

  public goToAC() {
    this.router.navigate(['/external', `${this.acUrl}home?ccEnabledClient=true&locationCode=${this.locationCode}&superUserNavigated=true`]);
  }

  private syncStatus(status: SyncResponseStatus) {

    if (status.type == "ValidationErrors" && status.errors.length > 0) {
      status.errors.forEach((err: any) => {
        if (this.validationErrorList.map((r) => r.resource).includes(err.MetaData.Resource)) {
          const errObjs = this.validationErrorList?.find(
            (r) => r.resource == err.MetaData.Resource
          )?.errorObjs;

          if (errObjs?.map((e) => e.id).includes(err.MetaData.Id)) {
            errObjs.find((e) => e.id == err.MetaData.Id)?.errors.push(err.Error);
          } else {
            errObjs?.push({
              id: err.MetaData.Id,
              errors: [err.Error],
            });
          }
        } else {
          this.validationErrorList.push({
            resource: err.MetaData.Resource,
            errorObjs: [{
              id: err.MetaData.Id,
              errors: [err.Error]
            }],
          });
        }
      });

      this.openSyncValidationResultPopup();
    }

    if (status.failed && status.failed > 0) this.syncHasErrors = true;

    if (status.status == "Job Complete") {
      this.syncInProgress = false;
      if (this.syncHasErrors) {
        this.syncService.syncCompletedWithErrors();
        this.syncHasErrors = false;
      } else {
        this.syncService.syncCompleted();
      }
    }

    if (status.status == "Failed To Start") {
      this.syncService.syncFailedToStart();
      this.syncInProgress = false;
    }
  }

  // -- Tenant change related methods
  // This method might called be twice because both event can occur at once too
  private _onReceiveOAuthEvents(event: OAuthEvent) {
    // alert('_onReceiveOAuthEvents');
    // This is when the token updated from the identity server
    if (event.type === 'token_received') {
      this._fetchInitialData();
      return;
    }

    // This is when the token loaded from the cache without a token request
    if (event.type === 'discovery_document_loaded' && this.oauthService.hasValidAccessToken()) {
      this._fetchInitialData();
    }
  }

  private _fetchInitialData() {
    this._fetchUserProfileData();
    this._fetchAvailableTenants();
    this._fetchUrlCode();
    this._fetchExternalClientConfiguration();
    this._fetchTenantLogoUrl();
    this._fetchConferencingProvider();
  }

  private _fetchConferencingProvider() {
    this.store.dispatch(CoreActions.initializeVirtualConferencingProvider());
  }

  private _fetchAvailableTenants() {
    this.store.dispatch(CoreActions.loadAvailableTenants());
  }

  private _fetchUserProfileData() {
    this.store.dispatch(CoreActions.loadUserInfo());
  }

  private _fetchUrlCode() {
    this.store.dispatch(CoreActions.loadUrlCode());
  }

  private _fetchExternalClientConfiguration() {
    this.store.dispatch(CoreActions.loadACClientConfiguration());
  }

  private _fetchTenantLogoUrl() {
    this.store.dispatch(CoreActions.loadTenantLogo());
  }

  private _subscribeToSyncStatus(tenantId: number, userId: number) {
    this.store.dispatch(CoreActions.subscribeToSyncStatus({ payload: { TenantId: tenantId, UserId: userId } }));
  }


  private _onReceiveUserName(username: string) {
    this.loggedUserName = username;
  }

  private _onReceiveUserId(userId: number) {
    this.loggedUserId = userId;
  }
  private _onSignInLocationChanged(lastAutoWrapupTime: string) {
    this.locationLastAutoWrapUpTime = lastAutoWrapupTime;
    this.loadBcsNotifications();
  }

  // Explicitly call this method to show update the visibility of the elements updated with properties
  // Added this method to increase the performance
  private _refreshShowHideElements(): void {
    if (this.availableTenants.length > 1) {
      this.showTenantChangeOption = true;
    } else {
      this.showTenantChangeOption = false;
    }
  }

  private _onAvailableTenantsChange(tenants: TenantModel[]): void {
    this.availableTenants = tenants;
    this._refreshShowHideElements();
  }

  private _onSelectedTenantChange(tenant: TenantModel): void {
    this.selectedTenant = tenant;

    if (this.prevTenantId != tenant.tenantID) {
      this.loadBcsNotifications();
      this.prevTenantId = tenant.tenantID;
    }

    if (tenant.tenantID != undefined)
      this.syncInitialization(tenant.tenantID.toString());
  }

  public showTenantChange() {
    if (this.showTenantChangeOption) {
      this.tenantChangeOpened = true;
    }
  }

  public closeTenantChange() {
    this.tenantChangeOpened = false;
  }

  // -- Tenant Change related methods ends
  public toggleSidebar() {
    this.globals.sidebar_collapsed = !this.globals.sidebar_collapsed;
  }

  public dropdownChanged(status: boolean): void {
    if (status === true && this.alertCount > 0) {
      this.alertReadingSubscription = this.bcsNotifications$.subscribe(notifications => {
        if (notifications != null) {
          notifications.messages.forEach(n => {
            if (n.isMessageRead === false) {
              this.notificationService.readAlert(n.messageId).subscribe(() => {
              }, (err) => {
                console.error(err);
              });
            }
          });
        }
      });
    }

    if (status === false && this.alertCount > 0) {
      this.alertReadingSubscription.unsubscribe();
      this.loadBcsNotifications();
    }
  }

  /**
 * Constructs HTML markup for an iframe to display notification messages.
 * @param notifications - NotificationMessageModel containing messages and alerts.
 * @returns Object - Contains the HTML markup and counts of different alert types.
 */
  private constructIframeMarkup(notifications: NotificationMessageModel) {
    const unreadMessageCount = notifications.messages.filter(m => !m.isMessageRead).length;
    const waitTimeAlertCount = notifications.messages.filter(m => m.ltsAlertDto.alertType == 'WaitAlert' && !m.isMessageRead).length;
    const immediateAlertCount = notifications.messages.filter(m => m.ltsAlertDto.alertType == 'ImmediateAlert' && !m.isMessageRead).length;
    const interactionThresholdAlertCount = notifications.messages.filter(m => m.ltsAlertDto.alertType == 'InteractionThresholdAlert' && !m.isMessageRead).length;

    const counts = {
      waitTime: waitTimeAlertCount,
      immediate: immediateAlertCount,
      interaction: interactionThresholdAlertCount
    }

    const markup = `
    <html id="browserAlertMarkup">
      <head>
        <style>
          @import url(https://fonts.googleapis.com/css?family=Poppins:100,300,400,500,700,900&display=swap);
          body,html {
            margin: 10;
            padding: 0;
            font-family: 'Poppins', sans-serif;
          };
        </style>
      </head>
      <title>FMSI OneCX</title>
      <body>
      <p>You have ${unreadMessageCount} new FMSI OneCX alert(s)!</p>
      <ul>
        ${waitTimeAlertCount > 0 ? `<li>Wait Time Alert (${waitTimeAlertCount})</li>` : ''}
        ${immediateAlertCount > 0 ? `<li>Check In Alert (${immediateAlertCount})</li>` : ''}
        ${interactionThresholdAlertCount > 0 ? `<li>Interaction Threshold Alert (${interactionThresholdAlertCount})</li>` : ''}
      </ul>
      </body>
    </html>
    `;

    return { markup, counts };
  }

  /**
 * Loads BCS notifications and triggers audio and visual alerts for unread messages.
 * Pre-requisites like selected tenant and user ID are checked before proceeding.
 * Dispatches action to load notifications and subscribes to the updates.
 * Plays alert tones and opens a popup window if conditions are met.
 */
  private loadBcsNotifications() {

    if (!(this.selectedTenant && this.selectedTenant.tenantID > 0 && this.loggedUserId > 0 && this.loggedUserTzOffset !== -1)) {
      return;
    }
    this.store.dispatch(AppHeaderActions.loadBcsNotifications({
      tenantId: this.selectedTenant.tenantID,
      userId: this.loggedUserId,
      fromUtcDate: moment
        .utc()
        .utcOffset(this.loggedUserTzOffset)
        .hour(0)
        .minute(0)
        .seconds(0)
        .utcOffset(0)
        .format('yyyy-MM-DDTHH:mm:ss')

    }));//Correctly fill this
    const bcsNotifSub = this.bcsNotifications$.subscribe(notifications => {
      if (notifications == null) {
        return;
      }
      const unreadnotif = notifications.messages.filter(n => n.isMessageRead === false);
      if (unreadnotif.length > 0 && (unreadnotif.length != this.alertCount)) {
        const audio = new Audio();
        let userDefaultAlertTone = this.lobbyConfigurations.find(((ac: SystemConfigValue) => ac.configurationName === SystemConfigurationName.DefaultAlertSound))?.value;
        if (userDefaultAlertTone == null || userDefaultAlertTone == "") {
          userDefaultAlertTone = 'Correct Answer Tone.wav';
        }
        audio.src = `./assets/audio/${userDefaultAlertTone}`;
        if ((!document.hasFocus()) && this.showAlertPopup) {
          audio.onended = () => {
            audio.onended = null;

            const markupInfo = this.constructIframeMarkup(notifications);
            const counts = markupInfo.counts;

            // window layout start
            const window_width = 450;
            const window_height = (counts.waitTime > 0 && counts.immediate > 0 && counts.interaction > 0) ? 170 : 140;

            let window_top;
            let window_left;

            // Page is minimized / another tab
            window_top = window.innerHeight - window_height;
            window_left = window.innerWidth - window_width;

            // window layout end

            const iframe = markupInfo.markup;

            const win = window.open(""
              , "Browser Alert"
              , `toolbar=no,scrollbars=no,resizable=no,top=${window_top},left=${window_left},width=${window_width},height=${window_height}`);
            win?.document.write(iframe);
            win?.document.close();
          }
        }
        audio.load();
        audio.play();
      }
      this.alertCount = unreadnotif.length;
    });
    this.subs.add(bcsNotifSub);
  }

  public logout() {
    // bug fix for BCDT-142 //
    /* solution tries to signout user first before logging out, this way even
     * if the user has a lobby record requested for him waiting in lobby
     * or if hes the last person to signed in also
     * he cant logout 
     * */
    this.store.select(getSignedInStatus).pipe(take(1))
      .subscribe((status: any) => {
        if (status != null && (status.statusId === UserSigninStatus.Idle || status.statusId === UserSigninStatus.Assisting)) {
          this._signOut();
        }
        else {
          this.gloabDataService.logout();
        }
      });
  }

  private _signOut() {
    const combinedsubs = combineLatest(
      [
        this.store.select(getCurrentTenant),
        this.store.select(getSignedInStatus),
        this.store.select(getLoggedInUserId),
        this.store.select(getLocationSelected)
      ]).pipe(
        map(([tenant, signedInStatus, userId, location]) => {

          if (signedInStatus?.userId == null && signedInStatus?.locationId == null) {
            this.unsignedStatus = {
              userId: userId || 0,
              locationId: location.homeLocationId || 0,
              statusId: 3,
              status: 'Offline',
              statusReason: '',
              currentAssistingLobbyEntryId: 0,
              locationCode: '',
              currentAssistingLocationId: 0,
              impersonateByName: null,
              impersonateById: null,
              isImpersonateOtherUser: false,
              impersonationTransferDetails: null
            }
          }

          return {
            tenantId: tenant.tenantID,
            userId: signedInStatus?.userId ?? userId,
            locationId: signedInStatus?.locationId ?? location.homeLocationId,
            reason: ''
          }
        }),
        take(1)
      );

    this.session.clearSignInSession();
    this.session.clearLobbyLocation();
    combinedsubs.subscribe(payload =>
      this._dispatchSignoutAction(payload)
    );

  }

  private _dispatchSignoutAction(payload: any) {
    this.store.dispatch(CoreActions.userLogOut({ payload }))
  }

  gotMyAccount() {
    this.router.navigate(['/master/my-profile']);
  }

  gotoMyAlerts() {
    this.router.navigate(['/admin/my-alerts-configuration/my-alerts-configuration-grid']);
  }

  gotoGlobalAlerts() {
    this.router.navigate(['/admin/global-alerts-configuration/global-alerts-configuration-grid']);
  }

  private syncAll() {
    this.syncInProgress = true;
    this.syncService.syncStarted()

    const syncSub = this.syncService.sync().subscribe({
      error: () => {
        this.syncService.syncFailedToStart()
        this.syncInProgress = false;
      }
    });

    this.subs.add(syncSub);
  }

  public openSyncPopup() {
    this.syncConfPopupOpen = true;
  }

  public closeSyncPopup(cmd: 'yes' | 'no') {
    this.syncConfPopupOpen = false;
    if (cmd == 'yes') {
      this.syncAll();
    }
  }

  public openSyncValidationResultPopup() {
    this.showSyncValidationResultPopup = true;
  }

  public closeSyncValidationResultPopup() {
    this.showSyncValidationResultPopup = false;
    this.validationErrorList = [];
  }

  mouseEnter(){
    if(this.userPermissionGroup == 'FMSI Default Group')
      this.clientToolTip = this.translateService.instant('_Shared_App_TenantChange_Change_Client_Text_');

  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
